import {FC, useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import Button from 'components/Button';
import Chain from 'components/Layout/Chain';
import {useTooltip} from 'components/Tooltip/hook';
import useFormError from 'hooks/useFormErrors';
import Field from '../Field';
import {FormContextString, InputProps} from '../types';
import {getRegisterRules} from '../utils';
import {
    PostcodeAutoFillErrorResponse,
    useGetPostcodeAutoFill,
} from './services';
import {PostcodeAutofill} from './types';
import {cleanPostcode, isValidPostcode} from './utils';

type InputPostcodeProps = InputProps & {
    autoFillLabel?: string;
    onAutofill?: (data: PostcodeAutofill) => void;
    showAutofill?: boolean;
};

const InputPostcode: FC<InputPostcodeProps> = ({
    autoFillLabel,
    className,
    classNameInput,
    disabled,
    error,
    helpText,
    id,
    label,
    name,
    onAutofill,
    readOnly,
    required,
    requiredType,
    rules,
    showAutofill = true,
    ...rest
}) => {
    const {setTooltipRef} = useTooltip();

    const {
        clearErrors,
        formState: {errors},
        register,
        setError,
        watch,
    } = useFormContext<FormContextString>();

    const registerRules = getRegisterRules({disabled, required, rules});

    const {ref, ...registration} = register(name, registerRules);

    const {t} = useTranslation();

    const postcode = watch(name);
    const {mutateAsync} = useGetPostcodeAutoFill();

    const fieldError = useFormError(name, error);

    useEffect(() => {
        if (errors[name]) clearErrors(name);
    }, [clearErrors, errors, name, postcode]);

    const onSubmitPostcode = () => {
        clearErrors(name);
        mutateAsync(cleanPostcode(postcode))
            .then((response) => {
                if (response?.data) {
                    onAutofill?.(response.data);
                }
            })
            .catch((serverError: PostcodeAutoFillErrorResponse) => {
                if ('fieldErrors' in serverError) {
                    const serverFieldError =
                        serverError.getFieldError('nonFieldErrors');
                    if (serverFieldError)
                        setError(name, serverFieldError.value);
                } else {
                    setError(name, {
                        message: serverError.message,
                        type: 'validate',
                    });
                }
            });
    };

    return (
        <Field
            className={className}
            disabled={registerRules?.disabled || readOnly}
            error={fieldError}
            helpText={helpText}
            label={label}
            name={name}
            required={!!registerRules?.required}
            requiredType={requiredType}
            type="input"
        >
            <Chain
                className={clsx(
                    'input-wrapper',
                    fieldError && 'input-invalid',
                    disabled && 'disabled',
                )}
            >
                <input
                    ref={(r) => {
                        ref(r);
                        setTooltipRef(r);
                    }}
                    aria-label={
                        rest['aria-label'] || label === null
                            ? undefined
                            : typeof label === 'string'
                              ? label
                              : name
                    }
                    className={clsx('border-none', classNameInput)}
                    id={id || name}
                    readOnly={readOnly}
                    {...registration}
                    tabIndex={readOnly ? -1 : undefined}
                    type="text"
                    {...rest}
                />
                {showAutofill && (
                    <Button
                        className="rounded-sm"
                        disabled={!isValidPostcode(postcode)}
                        onClick={onSubmitPostcode}
                    >
                        {autoFillLabel || t('form:postcodeAutoFill')}
                    </Button>
                )}
            </Chain>
        </Field>
    );
};

export default InputPostcode;
